
// ============================================================
// DEBUTS CROP
// WORKERS MANAGER
// PREMIUM NEON / RETRO TROPICAL
// ============================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  Clock3,
  Download,
  Eye,
  Filter,
  LoaderCircle,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Truck,
  Users,
  Wallet,
  X,
} from "lucide-react";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import "../../styles/WorkerManager.css";

// ============================================================
// CONFIGURACIÓN API
// ============================================================

const API_BASE_URL = (
  import.meta.env.VITE_API_URL || "/api"
).replace(/\/+$/, "");

const API_ENDPOINTS = {
  workers: "/workers/",
  workerStores: "/worker-stores/",
  deliveries: "/deliveries/",
  stores: "/stores/",
};

const ROLE_OPTIONS = [
  { value: "manager", label: "Administrador de tienda" },
  { value: "cashier", label: "Cajero" },
  { value: "delivery", label: "Repartidor" },
  { value: "staff", label: "Personal" },
];

const EMPTY_WORKER = {
  user: "",
  document_number: "",
  position: "",
  department: "",
  phone: "",
  emergency_contact: "",
  emergency_phone: "",
  base_salary: "0",
  is_active: true,
  notes: "",
};

const EMPTY_ASSIGNMENT = {
  worker: "",
  store: "",
  role: "staff",
  shift_start: "09:00",
  shift_end: "18:00",
  work_days: [],
  commission_percentage: "0",
  is_active: true,
};

const DAYS = [
  { value: "monday", label: "Lun" },
  { value: "tuesday", label: "Mar" },
  { value: "wednesday", label: "Mié" },
  { value: "thursday", label: "Jue" },
  { value: "friday", label: "Vie" },
  { value: "saturday", label: "Sáb" },
  { value: "sunday", label: "Dom" },
];

const STATUS_LABELS = {
  pending: "Pendiente",
  assigned: "Asignada",
  accepted: "Aceptada",
  picked_up: "Recogida",
  on_the_way: "En camino",
  arrived: "En destino",
  delivered: "Entregada",
  failed: "Fallida",
  cancelled: "Cancelada",
};

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------

function getAuthHeaders() {
  let token = "";

  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");

    token =
      auth.access ||
      auth.access_token ||
      auth.token ||
      "";
  } catch {
    token = "";
  }

  return {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;

  // Admite respuestas anidadas como:
  // { results: { success: true, data: [...] } }
  if (Array.isArray(payload?.results?.data)) {
    return payload.results.data;
  }

  return [];
}

function money(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function getErrorMessage(data, fallback) {
  if (typeof data === "string") return data;
  if (data?.detail) return data.detail;
  if (data?.message) return data.message;

  if (data && typeof data === "object") {
    const messages = Object.entries(data)
      .map(([key, value]) => {
        const text = Array.isArray(value)
          ? value.join(", ")
          : String(value);

        return `${key}: ${text}`;
      })
      .slice(0, 5);

    if (messages.length) return messages.join("\n");
  }

  return fallback;
}

function getWorkerName(worker) {
  return (
    worker?.full_name ||
    worker?.username ||
    `Trabajador #${worker?.id ?? "—"}`
  );
}

function getInitials(name) {
  return String(name || "DC")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function downloadCSV(rows, filename) {
  const escape = (value) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = [
    rows.headers.map(escape).join(";"),
    ...rows.data.map((row) => row.map(escape).join(";")),
  ].join("\r\n");

  const blob = new Blob(["\uFEFF", csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

// ------------------------------------------------------------
// COMPONENTE PRINCIPAL
// ------------------------------------------------------------

export default function WorkersManager() {
  const [workers, setWorkers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [stores, setStores] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("workers");

  const [workerModal, setWorkerModal] = useState(false);
  const [assignmentModal, setAssignmentModal] = useState(false);
  const [detailWorker, setDetailWorker] = useState(null);

  const [editingWorker, setEditingWorker] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);

  const [workerForm, setWorkerForm] = useState(EMPTY_WORKER);
  const [assignmentForm, setAssignmentForm] =
    useState(EMPTY_ASSIGNMENT);

  const [deliveryFilter, setDeliveryFilter] = useState("all");

  // ----------------------------------------------------------
  // PETICIONES HTTP
  // ----------------------------------------------------------

  const request = useCallback(async (path, options = {}) => {
    const headers = {
      ...getAuthHeaders(),
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    const normalizedPath = path.startsWith("/")
      ? path
      : `/${path}`;

    const baseUrl = API_BASE_URL.replace(/\/+$/, "");
    const url = `${baseUrl}${normalizedPath}`;

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const contentType = response.headers.get("content-type") || "";
    const data = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      throw new Error(
        getErrorMessage(data, `Error HTTP ${response.status}`)
      );
    }

    return data;
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);

    setError("");

    try {
      const [
        workersResult,
        assignmentsResult,
        deliveriesResult,
        storesResult,
      ] = await Promise.allSettled([
        request(API_ENDPOINTS.workers),
        request(API_ENDPOINTS.workerStores),
        request(API_ENDPOINTS.deliveries),
        request(API_ENDPOINTS.stores),
      ]);

      if (workersResult.status === "fulfilled") {
        setWorkers(normalizeList(workersResult.value));
      } else {
        throw workersResult.reason;
      }

      if (assignmentsResult.status === "fulfilled") {
        setAssignments(normalizeList(assignmentsResult.value));
      } else {
        setAssignments([]);
      }

      if (deliveriesResult.status === "fulfilled") {
        setDeliveries(normalizeList(deliveriesResult.value));
      } else {
        setDeliveries([]);
      }

      if (storesResult.status === "fulfilled") {
        setStores(normalizeList(storesResult.value));
      } else {
        setStores([]);
      }
    } catch (err) {
      setError(
        err.message ||
          "No fue posible cargar la información de trabajadores."
      );
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----------------------------------------------------------
  // MÉTRICAS
  // ----------------------------------------------------------

  const metrics = useMemo(() => {
    const activeWorkers = workers.filter((worker) => worker.is_active);

    const activeAssignments = assignments.filter(
      (assignment) => assignment.is_active
    );

    const activeCouriers = new Set(
      activeAssignments
        .filter((assignment) => assignment.role === "delivery")
        .map((assignment) => String(assignment.worker))
    );

    const pendingDeliveries = deliveries.filter((delivery) =>
      [
        "pending",
        "assigned",
        "accepted",
        "picked_up",
        "on_the_way",
        "arrived",
      ].includes(delivery.status)
    );

    return {
      total: workers.length,
      active: activeWorkers.length,
      inactive: workers.length - activeWorkers.length,
      couriers: activeCouriers.size,
      assignments: activeAssignments.length,
      pendingDeliveries: pendingDeliveries.length,
    };
  }, [workers, assignments, deliveries]);

  // ----------------------------------------------------------
  // FILTROS
  // ----------------------------------------------------------

  const filteredWorkers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return workers.filter((worker) => {
      const name = getWorkerName(worker).toLowerCase();

      const matchesSearch =
        !query ||
        name.includes(query) ||
        String(worker.employee_code || "").toLowerCase().includes(query) ||
        String(worker.document_number || "").toLowerCase().includes(query) ||
        String(worker.phone || "").toLowerCase().includes(query) ||
        String(worker.position || "").toLowerCase().includes(query) ||
        String(worker.department || "").toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && worker.is_active) ||
        (statusFilter === "inactive" && !worker.is_active);

      const workerAssignments = assignments.filter(
        (item) => String(item.worker) === String(worker.id)
      );

      const matchesRole =
        roleFilter === "all" ||
        workerAssignments.some(
          (item) => item.role === roleFilter && item.is_active
        );

      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [workers, assignments, search, statusFilter, roleFilter]);

  const filteredDeliveries = useMemo(
    () =>
      deliveries.filter(
        (delivery) =>
          deliveryFilter === "all" ||
          delivery.status === deliveryFilter
      ),
    [deliveries, deliveryFilter]
  );

  // ----------------------------------------------------------
  // TRABAJADORES: CREAR / EDITAR
  // ----------------------------------------------------------

  function openCreateWorker() {
    setEditingWorker(null);
    setWorkerForm({ ...EMPTY_WORKER });
    setError("");
    setWorkerModal(true);
  }

  function openEditWorker(worker) {
    setEditingWorker(worker);

    setWorkerForm({
      user: worker.user ?? "",
      document_number: worker.document_number ?? "",
      position: worker.position ?? "",
      department: worker.department ?? "",
      phone: worker.phone ?? "",
      emergency_contact: worker.emergency_contact ?? "",
      emergency_phone: worker.emergency_phone ?? "",
      base_salary: String(worker.base_salary ?? "0"),
      is_active: Boolean(worker.is_active),
      notes: worker.notes ?? "",
    });

    setError("");
    setWorkerModal(true);
  }

  async function saveWorker(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        ...workerForm,
        base_salary: workerForm.base_salary || "0",
      };

      if (!payload.user) {
        delete payload.user;
      }

      const path = editingWorker
        ? `${API_ENDPOINTS.workers}${editingWorker.id}/`
        : API_ENDPOINTS.workers;

      const result = await request(path, {
        method: editingWorker ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });

      setWorkers((current) => {
        if (editingWorker) {
          return current.map((item) =>
            item.id === editingWorker.id ? result : item
          );
        }

        return [result, ...current];
      });

      setWorkerModal(false);

      setNotice(
        editingWorker
          ? "Trabajador actualizado correctamente."
          : "Trabajador creado correctamente."
      );

      await loadData(true);
    } catch (err) {
      setError(err.message || "No fue posible guardar el trabajador.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleWorker(worker) {
    const action = worker.is_active ? "desactivar" : "activar";

    if (!window.confirm(`¿Deseas ${action} a ${getWorkerName(worker)}?`)) {
      return;
    }

    setError("");
    setNotice("");

    try {
      await request(`${API_ENDPOINTS.workers}${worker.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          is_active: !worker.is_active,
        }),
      });

      setNotice(
        `Trabajador ${worker.is_active ? "desactivado" : "activado"} correctamente.`
      );

      await loadData(true);
    } catch (err) {
      setError(err.message || "No fue posible cambiar el estado.");
    }
  }

  // ----------------------------------------------------------
  // ASIGNACIONES POR TIENDA
  // ----------------------------------------------------------

  function openCreateAssignment(worker = null) {
    setEditingAssignment(null);

    setAssignmentForm({
      ...EMPTY_ASSIGNMENT,
      worker: worker?.id ? String(worker.id) : "",
      store: stores[0]?.id ? String(stores[0].id) : "",
    });

    setError("");
    setAssignmentModal(true);
  }

  function openEditAssignment(assignment) {
    setEditingAssignment(assignment);

    setAssignmentForm({
      worker: String(assignment.worker ?? ""),
      store: String(assignment.store ?? ""),
      role: assignment.role ?? "staff",
      shift_start: assignment.shift_start || "09:00",
      shift_end: assignment.shift_end || "18:00",
      work_days: Array.isArray(assignment.work_days)
        ? assignment.work_days
        : [],
      commission_percentage: String(
        assignment.commission_percentage ?? "0"
      ),
      is_active: Boolean(assignment.is_active),
    });

    setError("");
    setAssignmentModal(true);
  }

  async function saveAssignment(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        ...assignmentForm,
        worker: Number(assignmentForm.worker),
        store: Number(assignmentForm.store),
        commission_percentage:
          assignmentForm.commission_percentage || "0",
      };

      const path = editingAssignment
        ? `${API_ENDPOINTS.workerStores}${editingAssignment.id}/`
        : API_ENDPOINTS.workerStores;

      await request(path, {
        method: editingAssignment ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });

      setAssignmentModal(false);
      setNotice("Asignación guardada correctamente.");

      await loadData(true);
    } catch (err) {
      setError(
        err.message ||
          "No fue posible guardar la asignación. Verifica el endpoint y los permisos."
      );
    } finally {
      setSaving(false);
    }
  }

  // ----------------------------------------------------------
  // EXPORTAR
  // ----------------------------------------------------------

  function exportWorkers() {
    downloadCSV(
      {
        headers: [
          "Código",
          "Nombre",
          "Usuario",
          "Correo",
          "Documento",
          "Cargo",
          "Departamento",
          "Teléfono",
          "Salario base",
          "Estado",
        ],
        data: filteredWorkers.map((worker) => [
          worker.employee_code,
          getWorkerName(worker),
          worker.username,
          worker.email,
          worker.document_number,
          worker.position,
          worker.department,
          worker.phone,
          worker.base_salary,
          worker.is_active ? "Activo" : "Inactivo",
        ]),
      },
      "debutscrop-trabajadores.csv"
    );
  }

  function exportAssignments() {
    downloadCSV(
      {
        headers: [
          "Trabajador",
          "Tienda",
          "Rol",
          "Inicio",
          "Fin",
          "Días",
          "Comisión %",
          "Ventas",
          "Pedidos",
          "Estado",
        ],
        data: assignments.map((item) => [
          item.worker_name,
          item.store_name,
          item.role_display || item.role,
          item.shift_start,
          item.shift_end,
          Array.isArray(item.work_days)
            ? item.work_days.join(", ")
            : item.work_days,
          item.commission_percentage,
          item.total_sales,
          item.orders_count,
          item.is_active ? "Activo" : "Inactivo",
        ]),
      },
      "debutscrop-asignaciones.csv"
    );
  }

  // ----------------------------------------------------------
  // RENDER PRINCIPAL
  // ----------------------------------------------------------

  return (
    <ProfilePageLayout>
      <main className="dc-workers">
        <header className="dc-workers-hero">
          <div className="dc-workers-hero-copy">
            <span className="dc-workers-eyebrow">
              DEBUTS CROP / HUMAN RESOURCES
            </span>

            <h1>
              GESTIÓN DE <span>TRABAJADORES</span>
            </h1>

            <p>
              Personal, asignaciones, jornadas y operaciones de entrega
              desde un solo centro de control.
            </p>
          </div>

          <div className="dc-workers-hero-actions">
            <button
              type="button"
              className="dc-workers-btn dc-workers-btn-secondary"
              onClick={() => loadData()}
              disabled={loading}
            >
              <RefreshCw size={17} />
              Actualizar
            </button>

            <button
              type="button"
              className="dc-workers-btn dc-workers-btn-primary"
              onClick={openCreateWorker}
            >
              <Plus size={18} />
              Nuevo trabajador
            </button>
          </div>
        </header>

        {error && (
          <div className="dc-workers-alert dc-workers-alert-error">
            <X size={18} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Cerrar error"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-workers-alert dc-workers-alert-success">
            <Check size={18} />
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice("")}
              aria-label="Cerrar aviso"
            >
              <X size={17} />
            </button>
          </div>
        )}

        <section className="dc-workers-metrics">
          <Metric
            icon={<Users />}
            label="Total trabajadores"
            value={metrics.total}
            detail="Registros de personal"
          />
          <Metric
            icon={<Activity />}
            label="Personal activo"
            value={metrics.active}
            detail={`${metrics.inactive} inactivos`}
          />
          <Metric
            icon={<Truck />}
            label="Repartidores"
            value={metrics.couriers}
            detail="Asignaciones activas"
          />
          <Metric
            icon={<MapPin />}
            label="Entregas pendientes"
            value={metrics.pendingDeliveries}
            detail="Operaciones en proceso"
          />
        </section>

        <nav className="dc-workers-tabs">
          <button
            type="button"
            className={activeTab === "workers" ? "active" : ""}
            onClick={() => setActiveTab("workers")}
          >
            <Users size={17} />
            Trabajadores
            <span>{workers.length}</span>
          </button>

          <button
            type="button"
            className={activeTab === "assignments" ? "active" : ""}
            onClick={() => setActiveTab("assignments")}
          >
            <BriefcaseBusiness size={17} />
            Asignaciones
            <span>{assignments.length}</span>
          </button>

          <button
            type="button"
            className={activeTab === "deliveries" ? "active" : ""}
            onClick={() => setActiveTab("deliveries")}
          >
            <Truck size={17} />
            Entregas
            <span>{deliveries.length}</span>
          </button>
        </nav>

        {activeTab === "workers" && (
          <section className="dc-workers-panel">
            <div className="dc-workers-toolbar">
              <div>
                <h2>Directorio de personal</h2>
                <p>Consulta y administra el equipo de Debuts Crop.</p>
              </div>

              <button
                type="button"
                className="dc-workers-btn dc-workers-btn-secondary"
                onClick={exportWorkers}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>

            <div className="dc-workers-filters">
              <label className="dc-workers-search">
                <Search size={18} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar nombre, código, documento o teléfono..."
                />
              </label>

              <label className="dc-workers-select">
                <Filter size={16} />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>
                <ChevronDown size={15} />
              </label>

              <label className="dc-workers-select">
                <BriefcaseBusiness size={16} />
                <select
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                >
                  <option value="all">Todos los roles</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} />
              </label>
            </div>

            {loading ? (
              <LoadingState />
            ) : filteredWorkers.length === 0 ? (
              <EmptyState
                title="No hay trabajadores para mostrar"
                description="Cambia los filtros o registra el primer trabajador."
                action={
                  <button
                    type="button"
                    className="dc-workers-btn dc-workers-btn-primary"
                    onClick={openCreateWorker}
                  >
                    <Plus size={17} />
                    Crear trabajador
                  </button>
                }
              />
            ) : (
              <div className="dc-workers-table-wrap">
                <table className="dc-workers-table">
                  <thead>
                    <tr>
                      <th>Trabajador</th>
                      <th>Cargo</th>
                      <th>Contacto</th>
                      <th>Asignaciones</th>
                      <th>Salario base</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredWorkers.map((worker) => {
                      const workerAssignments = assignments.filter(
                        (item) =>
                          String(item.worker) === String(worker.id)
                      );

                      return (
                        <tr key={worker.id}>
                          <td>
                            <div className="dc-workers-person">
                              <div className="dc-workers-avatar">
                                {getInitials(getWorkerName(worker))}
                              </div>

                              <div className="dc-workers-person-info">
                                <strong>{getWorkerName(worker)}</strong>
                                <span>
                                  {worker.employee_code || `ID #${worker.id}`}
                                </span>
                                {worker.email && (
                                  <small>{worker.email}</small>
                                )}
                              </div>
                            </div>
                          </td>

                          <td>
                            <strong className="dc-workers-position">
                              {worker.position || "Sin cargo"}
                            </strong>
                            <span className="dc-workers-subtext">
                              {worker.department || "Sin departamento"}
                            </span>
                          </td>

                          <td>
                            <span className="dc-workers-contact">
                              <Phone size={14} />
                              {worker.phone || "Sin teléfono"}
                            </span>
                          </td>

                          <td>
                            <span className="dc-workers-count">
                              {workerAssignments.length}
                            </span>
                            <span className="dc-workers-subtext">
                              tienda(s)
                            </span>
                          </td>

                          <td className="dc-workers-money">
                            {money(worker.base_salary)}
                          </td>

                          <td>
                            <span
                              className={`dc-workers-status ${
                                worker.is_active ? "is-active" : "is-inactive"
                              }`}
                            >
                              <span />
                              {worker.is_active ? "Activo" : "Inactivo"}
                            </span>
                          </td>

                          <td>
                            <div className="dc-workers-row-actions">
                              <button
                                type="button"
                                title="Ver ficha"
                                aria-label="Ver ficha"
                                onClick={() => setDetailWorker(worker)}
                              >
                                <Eye size={17} />
                              </button>

                              <button
                                type="button"
                                title="Editar"
                                aria-label="Editar trabajador"
                                onClick={() => openEditWorker(worker)}
                              >
                                <Pencil size={17} />
                              </button>

                              <button
                                type="button"
                                title="Asignar tienda"
                                aria-label="Asignar tienda"
                                onClick={() => openCreateAssignment(worker)}
                              >
                                <BriefcaseBusiness size={17} />
                              </button>

                              <button
                                type="button"
                                title={worker.is_active ? "Desactivar" : "Activar"}
                                aria-label={
                                  worker.is_active
                                    ? "Desactivar trabajador"
                                    : "Activar trabajador"
                                }
                                onClick={() => toggleWorker(worker)}
                              >
                                {worker.is_active ? (
                                  <ShieldCheck size={17} />
                                ) : (
                                  <BadgeCheck size={17} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="dc-workers-panel-footer">
              Mostrando <strong>{filteredWorkers.length}</strong> de{" "}
              <strong>{workers.length}</strong> trabajadores
            </div>
          </section>
        )}

        {activeTab === "assignments" && (
          <section className="dc-workers-panel">
            <div className="dc-workers-toolbar">
              <div>
                <h2>Asignaciones por tienda</h2>
                <p>Control de roles, turnos, comisiones y ventas.</p>
              </div>

              <div className="dc-workers-inline-actions">
                <button
                  type="button"
                  className="dc-workers-btn dc-workers-btn-secondary"
                  onClick={exportAssignments}
                >
                  <Download size={16} />
                  Exportar
                </button>

                <button
                  type="button"
                  className="dc-workers-btn dc-workers-btn-primary"
                  onClick={() => openCreateAssignment()}
                >
                  <Plus size={17} />
                  Nueva asignación
                </button>
              </div>
            </div>

            {loading ? (
              <LoadingState />
            ) : assignments.length === 0 ? (
              <EmptyState
                title="No hay asignaciones"
                description="Asigna trabajadores a una tienda para configurar su rol y horario."
                action={
                  <button
                    type="button"
                    className="dc-workers-btn dc-workers-btn-primary"
                    onClick={() => openCreateAssignment()}
                  >
                    <Plus size={17} />
                    Crear asignación
                  </button>
                }
              />
            ) : (
              <div className="dc-workers-assignment-grid">
                {assignments.map((assignment) => (
                  <article
                    className="dc-workers-assignment-card"
                    key={assignment.id}
                  >
                    <div className="dc-workers-assignment-top">
                      <div className="dc-workers-assignment-icon">
                        <BriefcaseBusiness size={20} />
                      </div>

                      <span
                        className={`dc-workers-status ${
                          assignment.is_active ? "is-active" : "is-inactive"
                        }`}
                      >
                        <span />
                        {assignment.is_active ? "Activa" : "Inactiva"}
                      </span>
                    </div>

                    <h3>
                      {assignment.worker_name ||
                        getWorkerName(
                          workers.find(
                            (worker) =>
                              String(worker.id) === String(assignment.worker)
                          )
                        )}
                    </h3>

                    <p className="dc-workers-assignment-store">
                      <MapPin size={15} />
                      {assignment.store_name || `Tienda #${assignment.store}`}
                    </p>

                    <div className="dc-workers-role">
                      {assignment.role_display || assignment.role}
                    </div>

                    <div className="dc-workers-assignment-details">
                      <div>
                        <Clock3 size={16} />
                        <span>
                          {assignment.shift_start || "—"} –{" "}
                          {assignment.shift_end || "—"}
                        </span>
                      </div>

                      <div>
                        <Wallet size={16} />
                        <span>
                          Comisión: {assignment.commission_percentage || "0"}%
                        </span>
                      </div>

                      <div>
                        <Activity size={16} />
                        <span>Ventas: {money(assignment.total_sales)}</span>
                      </div>

                      <div>
                        <Truck size={16} />
                        <span>Pedidos: {assignment.orders_count ?? 0}</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="dc-workers-btn dc-workers-btn-secondary dc-workers-full-btn"
                      onClick={() => openEditAssignment(assignment)}
                    >
                      <Pencil size={16} />
                      Editar asignación
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === "deliveries" && (
          <section className="dc-workers-panel">
            <div className="dc-workers-toolbar">
              <div>
                <h2>Centro de entregas</h2>
                <p>Consulta el estado operativo de los repartos.</p>
              </div>

              <label className="dc-workers-select">
                <Filter size={16} />
                <select
                  value={deliveryFilter}
                  onChange={(event) => setDeliveryFilter(event.target.value)}
                >
                  <option value="all">Todos los estados</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} />
              </label>
            </div>

            {loading ? (
              <LoadingState />
            ) : filteredDeliveries.length === 0 ? (
              <EmptyState
                title="No hay entregas para mostrar"
                description="Las entregas domiciliarias aparecerán aquí cuando existan registros."
              />
            ) : (
              <div className="dc-workers-delivery-list">
                {filteredDeliveries.map((delivery) => (
                  <article
                    className="dc-workers-delivery-card"
                    key={delivery.id}
                  >
                    <div className="dc-workers-delivery-number">
                      <Truck size={20} />
                      <div>
                        <strong>
                          {delivery.checkout_number || `Entrega #${delivery.id}`}
                        </strong>
                        <span>
                          {delivery.store_name || `Tienda #${delivery.store}`}
                        </span>
                      </div>
                    </div>

                    <div className="dc-workers-delivery-customer">
                      <strong>{delivery.customer_name || "Cliente"}</strong>
                      <span>
                        {delivery.customer_phone || "Sin teléfono"}
                      </span>
                    </div>

                    <div className="dc-workers-delivery-address">
                      <MapPin size={16} />
                      <span>
                        {[
                          delivery.address,
                          delivery.neighborhood,
                          delivery.city,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Sin dirección"}
                      </span>
                    </div>

                    <div>
                      <strong>{delivery.worker_name || "Sin repartidor"}</strong>
                      <span className="dc-workers-subtext">
                        {delivery.route_order != null
                          ? `Orden de ruta: ${delivery.route_order}`
                          : "Ruta sin ordenar"}
                      </span>
                    </div>

                    <span
                      className={`dc-workers-delivery-status status-${delivery.status}`}
                    >
                      {STATUS_LABELS[delivery.status] ||
                        delivery.status_display ||
                        delivery.status}
                    </span>

                    {delivery.latitude != null &&
                      delivery.longitude != null && (
                        <a
                          className="dc-workers-map-link"
                          href={`https://www.google.com/maps?q=${encodeURIComponent(
                            `${delivery.latitude},${delivery.longitude}`
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <MapPin size={15} />
                          Ver mapa
                        </a>
                      )}
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      {/* =====================================================
          MODAL TRABAJADOR
      ===================================================== */}

      {workerModal && (
        <Modal
          title={editingWorker ? "Editar trabajador" : "Nuevo trabajador"}
          subtitle="Información laboral y datos de contacto"
          onClose={() => !saving && setWorkerModal(false)}
        >
          <form className="dc-workers-form" onSubmit={saveWorker}>
            <div className="dc-workers-form-grid">
              <Field label="ID de usuario Django">
                <input
                  type="number"
                  min="1"
                  value={workerForm.user}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      user: event.target.value,
                    })
                  }
                  placeholder="ID del usuario existente"
                  required={!editingWorker}
                />
              </Field>

              <Field label="Documento">
                <input
                  value={workerForm.document_number}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      document_number: event.target.value,
                    })
                  }
                  placeholder="Número de documento"
                />
              </Field>

              <Field label="Cargo">
                <input
                  value={workerForm.position}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      position: event.target.value,
                    })
                  }
                  placeholder="Ej. Asesor comercial"
                />
              </Field>

              <Field label="Departamento">
                <input
                  value={workerForm.department}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      department: event.target.value,
                    })
                  }
                  placeholder="Ej. Ventas"
                />
              </Field>

              <Field label="Teléfono">
                <input
                  type="tel"
                  value={workerForm.phone}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      phone: event.target.value,
                    })
                  }
                  placeholder="+57..."
                />
              </Field>

              <Field label="Salario base (COP)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={workerForm.base_salary}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      base_salary: event.target.value,
                    })
                  }
                  required
                />
              </Field>

              <Field label="Contacto de emergencia">
                <input
                  value={workerForm.emergency_contact}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      emergency_contact: event.target.value,
                    })
                  }
                  placeholder="Nombre del contacto"
                />
              </Field>

              <Field label="Teléfono de emergencia">
                <input
                  type="tel"
                  value={workerForm.emergency_phone}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      emergency_phone: event.target.value,
                    })
                  }
                  placeholder="+57..."
                />
              </Field>

              <Field label="Notas" full>
                <textarea
                  rows="3"
                  value={workerForm.notes}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      notes: event.target.value,
                    })
                  }
                  placeholder="Observaciones internas del trabajador"
                />
              </Field>

              <label className="dc-workers-checkbox-field">
                <input
                  type="checkbox"
                  checked={workerForm.is_active}
                  onChange={(event) =>
                    setWorkerForm({
                      ...workerForm,
                      is_active: event.target.checked,
                    })
                  }
                />
                <span>Trabajador activo</span>
              </label>
            </div>

            <div className="dc-workers-modal-actions">
              <button
                type="button"
                className="dc-workers-btn dc-workers-btn-secondary"
                onClick={() => setWorkerModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="dc-workers-btn dc-workers-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <LoaderCircle className="dc-workers-spin" size={17} />
                ) : (
                  <Check size={17} />
                )}
                {saving ? "Guardando..." : "Guardar trabajador"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* =====================================================
          MODAL ASIGNACIÓN
      ===================================================== */}

      {assignmentModal && (
        <Modal
          title={editingAssignment ? "Editar asignación" : "Asignar trabajador"}
          subtitle="Configura la relación entre trabajador y tienda"
          onClose={() => !saving && setAssignmentModal(false)}
        >
          <form className="dc-workers-form" onSubmit={saveAssignment}>
            <div className="dc-workers-form-grid">
              <Field label="Trabajador">
                <select
                  value={assignmentForm.worker}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      worker: event.target.value,
                    })
                  }
                  required
                >
                  <option value="">Seleccionar trabajador</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {getWorkerName(worker)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Tienda">
                <select
                  value={assignmentForm.store}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      store: event.target.value,
                    })
                  }
                  required
                >
                  <option value="">Seleccionar tienda</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name || `Tienda #${store.id}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Rol">
                <select
                  value={assignmentForm.role}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      role: event.target.value,
                    })
                  }
                  required
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Comisión (%)">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={assignmentForm.commission_percentage}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      commission_percentage: event.target.value,
                    })
                  }
                />
              </Field>

              <Field label="Inicio de turno">
                <input
                  type="time"
                  value={assignmentForm.shift_start}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      shift_start: event.target.value,
                    })
                  }
                  required
                />
              </Field>

              <Field label="Fin de turno">
                <input
                  type="time"
                  value={assignmentForm.shift_end}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      shift_end: event.target.value,
                    })
                  }
                  required
                />
              </Field>

              <Field label="Días laborales" full>
                <div className="dc-workers-days">
                  {DAYS.map((day) => {
                    const checked = assignmentForm.work_days.includes(
                      day.value
                    );

                    return (
                      <label
                        key={day.value}
                        className={checked ? "selected" : ""}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            const next = event.target.checked
                              ? [...assignmentForm.work_days, day.value]
                              : assignmentForm.work_days.filter(
                                  (value) => value !== day.value
                                );

                            setAssignmentForm({
                              ...assignmentForm,
                              work_days: next,
                            });
                          }}
                        />
                        {day.label}
                      </label>
                    );
                  })}
                </div>
              </Field>

              <label className="dc-workers-checkbox-field">
                <input
                  type="checkbox"
                  checked={assignmentForm.is_active}
                  onChange={(event) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      is_active: event.target.checked,
                    })
                  }
                />
                <span>Asignación activa</span>
              </label>
            </div>

            <div className="dc-workers-modal-actions">
              <button
                type="button"
                className="dc-workers-btn dc-workers-btn-secondary"
                onClick={() => setAssignmentModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="dc-workers-btn dc-workers-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <LoaderCircle className="dc-workers-spin" size={17} />
                ) : (
                  <Check size={17} />
                )}
                {saving ? "Guardando..." : "Guardar asignación"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* =====================================================
          FICHA DEL TRABAJADOR
      ===================================================== */}

      {detailWorker && (
        <Modal
          title="Ficha del trabajador"
          subtitle={getWorkerName(detailWorker)}
          onClose={() => setDetailWorker(null)}
        >
          <div className="dc-workers-detail">
            <div className="dc-workers-detail-head">
              <div className="dc-workers-avatar dc-workers-avatar-large">
                {getInitials(getWorkerName(detailWorker))}
              </div>

              <div>
                <h3>{getWorkerName(detailWorker)}</h3>
                <span>
                  {detailWorker.employee_code || `ID #${detailWorker.id}`}
                </span>
                <span
                  className={`dc-workers-status ${
                    detailWorker.is_active ? "is-active" : "is-inactive"
                  }`}
                >
                  <span />
                  {detailWorker.is_active ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>

            <div className="dc-workers-detail-grid">
              <Detail label="Correo" value={detailWorker.email} />
              <Detail label="Documento" value={detailWorker.document_number} />
              <Detail label="Cargo" value={detailWorker.position} />
              <Detail label="Departamento" value={detailWorker.department} />
              <Detail label="Teléfono" value={detailWorker.phone} />
              <Detail
                label="Salario base"
                value={money(detailWorker.base_salary)}
              />
              <Detail
                label="Contacto de emergencia"
                value={detailWorker.emergency_contact}
              />
              <Detail
                label="Teléfono de emergencia"
                value={detailWorker.emergency_phone}
              />
              <Detail
                label="Notas"
                value={detailWorker.notes}
                full
              />
            </div>

            <div className="dc-workers-detail-actions">
              <button
                type="button"
                className="dc-workers-btn dc-workers-btn-secondary"
                onClick={() => {
                  const worker = detailWorker;
                  setDetailWorker(null);
                  openEditWorker(worker);
                }}
              >
                <Pencil size={16} />
                Editar ficha
              </button>

              <button
                type="button"
                className="dc-workers-btn dc-workers-btn-primary"
                onClick={() => {
                  const worker = detailWorker;
                  setDetailWorker(null);
                  openCreateAssignment(worker);
                }}
              >
                <Plus size={16} />
                Asignar tienda
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ProfilePageLayout>
  );
}

// ------------------------------------------------------------
// COMPONENTES AUXILIARES
// ------------------------------------------------------------

function Metric({ icon, label, value, detail }) {
  return (
    <article className="dc-workers-metric">
      <div className="dc-workers-metric-top">
        <span className="dc-workers-metric-icon">{icon}</span>
        <span className="dc-workers-metric-dot" />
      </div>
      <span className="dc-workers-metric-label">{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function LoadingState() {
  return (
    <div className="dc-workers-loading">
      <LoaderCircle className="dc-workers-spin" size={28} />
      <span>Cargando información...</span>
    </div>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <div className="dc-workers-empty">
      <div className="dc-workers-empty-icon">
        <Users size={27} />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

function Modal({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="dc-workers-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="dc-workers-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dc-workers-modal-title"
      >
        <header className="dc-workers-modal-header">
          <div>
            <h2 id="dc-workers-modal-title">{title}</h2>
            <p>{subtitle}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar ventana"
          >
            <X size={20} />
          </button>
        </header>

        {children}
      </section>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label className={`dc-workers-field ${full ? "field-full" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value, full = false }) {
  return (
    <div className={`dc-workers-detail-item ${full ? "detail-full" : ""}`}>
      <span>{label}</span>
      <strong>{value || "No registrado"}</strong>
    </div>
  );
}
